import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PARTNER_CATEGORIES, pl } from "../../lib/partnerConstants";
import { Button } from "../ui/button";
import { Download, FileText, Building2, Trophy, Star, Medal } from "lucide-react";

const medal = ["#fbbf24", "#cbd5e1", "#d97706"];

export default function GroupReportTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [data, setData] = useState(null);
  const [scoring, setScoring] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [g, s] = await Promise.all([api.get("/crm/group-overview"), api.get("/crm/partner-scoring")]);
        setData(g.data); setScoring(s.data.partners || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const exportGroup = async (format) => {
    try {
      const res = await api.get(`/crm/export/group-overview`, { params: { format }, responseType: "blob" });
      const link = document.createElement("a"); link.href = URL.createObjectURL(res.data);
      link.download = format === "pdf" ? "pilotage-groupe.pdf" : "pilotage-groupe.xlsx"; link.click(); URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <p className="text-muted-foreground text-sm py-8">{t("crm.group.loading")}</p>;
  const rows = [...(data.maisons || []), data.totals];

  return (
    <div className="space-y-4" data-testid="crm-group-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-emerald-400" /> {t("crm.group.title")} {data.group_name && <span className="text-sm text-muted-foreground">· {data.group_name}</span>}</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportGroup("xlsx")} data-testid="crm-group-export-xlsx"><Download className="h-4 w-4 mr-1" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={() => exportGroup("pdf")} data-testid="crm-group-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
      </div>

      {/* Comparatif résidences */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="crm-group-comparison">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/10">
              <th className="p-3">{t("crm.group.residence")}</th>
              <th className="p-3 text-center">{t("crm.group.occupancy")}</th>
              <th className="p-3 text-center">{t("crm.group.prospects")}</th>
              <th className="p-3 text-center">{t("crm.group.admissions")}</th>
              <th className="p-3 text-center">{t("crm.group.conversion")}</th>
              <th className="p-3 text-center">{t("crm.group.events")}</th>
              <th className="p-3 text-center">{t("crm.group.eventAdmissions")}</th>
              <th className="p-3 text-center">{t("crm.group.satisfaction")}</th>
              <th className="p-3 text-center">{t("crm.group.partnerships")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const isTotal = idx === rows.length - 1;
              return (
                <tr key={r.maison_id || "total"} className={`border-b border-white/5 ${isTotal ? "font-semibold bg-emerald-500/[0.06]" : "hover:bg-white/[0.02]"}`} data-testid={`crm-group-row-${isTotal ? "total" : r.maison_id}`}>
                  <td className="p-3">{isTotal ? t("crm.group.totalGroup") : r.maison}</td>
                  <td className="p-3 text-center">{r.occupancy_rate}%</td>
                  <td className="p-3 text-center">{r.prospects_total}</td>
                  <td className="p-3 text-center text-emerald-400">{r.admissions}</td>
                  <td className="p-3 text-center">{r.conversion}%</td>
                  <td className="p-3 text-center text-pink-400">{r.events || 0}</td>
                  <td className="p-3 text-center">{r.event_admissions || 0}</td>
                  <td className="p-3 text-center">{r.satisfaction ? <span className="inline-flex items-center gap-0.5 text-amber-400">{r.satisfaction} <Star className="h-3 w-3 fill-amber-400" /></span> : "—"}</td>
                  <td className="p-3 text-center text-teal-300">{r.partnerships || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Scoring partenaires */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <h4 className="font-display text-base font-semibold mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-400" /> {t("crm.group.scoringTitle")}</h4>
        {scoring.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">{t("crm.group.noPartner")}</p> : (
          <div className="space-y-1.5" data-testid="crm-group-scoring">
            {scoring.map((p, i) => {
              const cat = PARTNER_CATEGORIES[p.category];
              return (
                <div key={p.id} className="flex items-center gap-3 rounded-xl border border-white/10 p-2.5 bg-white/[0.02]" data-testid={`crm-scoring-${p.id}`}>
                  <span className="w-7 text-center font-bold" style={{ color: i < 3 ? medal[i] : "#64748b" }}>{i < 3 ? <Medal className="h-4 w-4 inline" /> : i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">{cat ? pl(lang, cat) : p.category} · {p.orientations} {t("crm.group.orientations")} · {p.admissions} {t("crm.group.adm")} · {p.satisfaction ? `${p.satisfaction}/5` : "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-xl font-bold text-emerald-400">{p.score}</div>
                    <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden mt-0.5"><div className="h-full rounded-full" style={{ width: `${Math.min(100, p.score)}%`, background: "linear-gradient(90deg,#34d399,#fbbf24)" }} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
